/*
 * List.tsx  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useRef } from "react";

import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";

import { useForkRef } from "../hooks/useForkRef";
import { Key } from "ts-key-enum";

export interface IListProps extends React.ComponentPropsWithRef<"ul"> {
	className?: string;
	children?: React.ReactNode;
	autoFocus?: boolean;
	autoFocusItem?: boolean;
	disabledItemsFocusable?: boolean;
	disableListWrap?: boolean;
	variant?: "menu" | "selectedMenu" | undefined | null; // menu - focus on first item; selectedMenu - focus on selected item,
	Component?: React.ElementType;
}

function getOwnerDocument(node): Document {
	return (node && node.ownerDocument) || document;
}

function nextItem(list, item, disableListWrap) {
	if (list === item) {
		return list.firstChild;
	}
	if (item && item.nextElementSibling) {
		return item.nextElementSibling;
	}
	return disableListWrap ? null : list.firstChild;
}

function previousItem(list, item, disableListWrap) {
	if (list === item) {
		return disableListWrap ? list.firstChild : list.lastChild;
	}
	if (item && item.previousElementSibling) {
		return item.previousElementSibling;
	}
	return disableListWrap ? null : list.lastChild;
}

function moveFocus(list, currentFocus, disableListWrap, disabledItemsFocusable, traversalFunction) {
	let wrappedOnce = false;
	let nextFocus = traversalFunction(list, currentFocus, currentFocus ? disableListWrap : false);

	while (nextFocus) {
		// Prevent infinite loop.
		if (nextFocus === list.firstChild) {
			if (wrappedOnce) {
				return;
			}
			wrappedOnce = true;
		}

		// Same logic as useAutocomplete.js
		const nextFocusDisabled = disabledItemsFocusable
			? false
			: nextFocus.disabled || nextFocus.getAttribute("aria-disabled") === "true";

		if (!nextFocus.hasAttribute("tabindex") || nextFocusDisabled) {
			// Move to the next element.
			nextFocus = traversalFunction(list, nextFocus, disableListWrap);
		} else {
			nextFocus.focus();
			return;
		}
	}
}

export const List = React.forwardRef<HTMLUListElement, IListProps>(
	(
		{
			children,
			Component,
			className,
			autoFocus,
			autoFocusItem,
			variant,
			onKeyDown,
			disabledItemsFocusable,
			disableListWrap,
			...props
		},
		ref
	) => {
		const baseClass = BEM("edt-list");
		const listRef = useRef<HTMLUListElement>();
		const forkRef = useForkRef(listRef, ref);
		let activeItemIndex = autoFocus ? 0 : -1;

		if (className) {
			baseClass.add(className);
		}

		const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLUListElement>>(
			(event) => {
				const list = listRef.current;
				/**
				 * @type {Element} - will always be defined since we are in a keydown handler
				 * attached to an element. A keydown event is either dispatched to the activeElement
				 * or document.body or document.documentElement. Only the first case will
				 * trigger this specific handler.
				 */
				const currentFocus = getOwnerDocument(list).activeElement;

				switch (event.key) {
					case Key.ArrowDown:
						// Prevent scroll of the page
						event.preventDefault();
						moveFocus(list, currentFocus, disableListWrap, disabledItemsFocusable, nextItem);
						break;
					case Key.ArrowUp:
						event.preventDefault();
						moveFocus(list, currentFocus, disableListWrap, disabledItemsFocusable, previousItem);
						break;
					case Key.Home:
						event.preventDefault();
						moveFocus(list, null, disableListWrap, disabledItemsFocusable, nextItem);
						break;
					case Key.End:
						event.preventDefault();
						moveFocus(list, null, disableListWrap, disabledItemsFocusable, previousItem);
						break;
				}

				if (isFunction(onKeyDown)) {
					onKeyDown(event);
				}
			},
			[ onKeyDown ]
		);

		React.Children.forEach(children, (child, index) => {
			if (!React.isValidElement(child)) {
				return;
			}

			if (!child.props.disabled) {
				if (variant === "selectedMenu" && child.props.selected) {
					activeItemIndex = index;
				} else if (activeItemIndex === -1) {
					activeItemIndex = index;
				}
			}
		});

		const items = React.Children.map(children, (child, index) => {
			if (!React.isValidElement(child)) {
				return null;
			}

			if (index === activeItemIndex) {
				const newChildProps: { autoFocus?: boolean; tabIndex?: 0 | -1 } = {};
				if (autoFocusItem) {
					newChildProps.autoFocus = true;
				}
				if (child.props.tabIndex === undefined && variant === "selectedMenu") {
					newChildProps.tabIndex = 0;
				}

				return React.cloneElement(child, newChildProps);
			}

			return child;
		});

		return (
			<Component
				className={baseClass.toClassName()}
				ref={forkRef}
				tabIndex={autoFocus ? 0 : -1}
				onKeyDown={handleKeyDown}
				{...props}
			>
				{items}
			</Component>
		);
	}
);

List.defaultProps = {
	role: "listbox",
	autoFocus: false,
	autoFocusItem: false,
	disabledItemsFocusable: false,
	disableListWrap: false,
	Component: "ul"
};
